import { useCallback, useEffect, useRef, useState } from 'react';
import { ASSISTANT_GREETING, sendAssistantMessage } from '../services/aiService';
import { isAiApiConfigured } from '../services/endpoints';

/** Message authors. Strings keep the transcript serialisable for future logging. */
const ROLE_ASSISTANT = 'assistant';
const ROLE_VISITOR = 'visitor';

/**
 * Owns the assistant conversation: transcript, input, sending state, and the
 * repeat-answer cache.
 *
 * Keeping this in a hook means AssistantConversation stays a rendering
 * component, and the widget owns the state rather than the modal panel -- which
 * is what lets the transcript survive the panel being closed and reopened.
 *
 * Answers are cached in memory for the life of the page, keyed on the exact
 * question, for the **first turn of a conversation only**. See the gate in
 * `send`: a repeat of an opening question is common on a marketing site and
 * costs no model tokens, while a repeat once a conversation is under way means
 * something else and must reach the Lambda with its history.
 */
export const useAssistantChat = () => {
  const [messages, setMessages] = useState(() => [
    { id: 'greeting', role: ROLE_ASSISTANT, text: ASSISTANT_GREETING },
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Neither of these should trigger a render.
  const cacheRef = useRef(new Map());
  const nextIdRef = useRef(0);
  const abortRef = useRef(null);

  // Cancel an in-flight request if the visitor navigates away mid-question, so
  // a late reply cannot land on an unmounted tree.
  useEffect(() => () => abortRef.current?.abort(), []);

  const append = useCallback((role, text, extra = {}) => {
    // The id is generated here, at call time, rather than inside the updater
    // below. React may run an updater later (or twice under StrictMode), and
    // reading the counter there would give two pending messages the same key.
    nextIdRef.current += 1;
    const id = `message-${nextIdRef.current}`;

    setMessages((previous) => [...previous, { id, role, text, ...extra }]);
  }, []);

  const send = useCallback(
    async (rawQuestion) => {
      const question = String(rawQuestion ?? '').trim();

      // An empty question, or a second one while the first is in flight, would
      // post noise and duplicate answers into the transcript.
      if (!question || isSending) return;

      setInput('');

      // The answer cache serves the first question of a conversation only.
      // Cached answers are keyed on the visitor's exact words and know nothing
      // about the conversation around them, so once a conversation is under way
      // the same words can mean something different -- "Yes" answering a booking
      // prompt is not "Yes" answering a pricing question -- and a cached reply
      // would be wrong. Past the first turn every message reaches the Lambda,
      // whose history is now the source of context.
      const isFirstTurn = !messages.some(
        (entry) => entry.role === ROLE_VISITOR
      );

      const cached = isFirstTurn ? cacheRef.current.get(question) : null;
      if (cached) {
        append(ROLE_VISITOR, question);
        append(ROLE_ASSISTANT, cached);
        return;
      }

      append(ROLE_VISITOR, question);
      setIsSending(true);

      const controller = new AbortController();
      abortRef.current = controller;

      // The transcript is the only record of the conversation, so it travels
      // with the next turn. This `messages` is the state committed by the render
      // the visitor clicked Send in, so it holds every turn before this one;
      // aiService appends the question being asked now.
      const outcome = await sendAssistantMessage(question, {
        signal: controller.signal,
        history: messages,
      });

      if (controller.signal.aborted) return;

      setIsSending(false);

      const reply = outcome.status === 'ok' ? outcome.message.trim() : '';

      if (reply) {
        // Only a real answer to the first question is cached. An error or an
        // empty reply would poison the cache for that question for the rest of
        // the visit, and an answer given mid-conversation belongs to the
        // conversation that produced it -- the same words later are a different
        // question.
        if (isFirstTurn) cacheRef.current.set(question, reply);
        append(ROLE_ASSISTANT, reply);
      } else {
        append(ROLE_ASSISTANT, outcome.message, { isError: true });
      }
    },
    // The turn carries the conversation so far, so the transcript is part of
    // what `send` closes over.
    [append, isSending, messages]
  );

  return {
    messages,
    input,
    setInput,
    isSending,
    send,
    isConfigured: isAiApiConfigured(),
  };
};

export default useAssistantChat;
